import React, { useEffect, useState } from "react";
import { Button, Empty, Modal, Popconfirm, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import ProtocolServices from "../../services/ProtocolServices";
import AddProtocolCondition from "../Modals/AddProtocolCondition";
import _, { set } from "lodash";
import { translate } from "../../locale";
import { TableHelper } from "../../helpers/TableHelper";
import PropTypes from "prop-types";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const ProtocolsActionDaysTable = ({
  id,
  reload,
  onChange,
  onDelete,
  onDeleteCondition,
  loading,
  enterprises,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedPill, setSelectedPill] = useState(null);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });

  const [total, setTotal] = useState(0);
  const rpm = {
    diabetes: translate("Glucemia"),
    hypertension: translate("Presión arterial"),
    weight: translate("Peso"),
    feeding: translate("Alimentación"),
    o2saturation: translate("Saturación O2"),
    temperature: translate("Temperatura"),
    water: translate("Agua"),
    steps: translate("Pasos"),
    physical_activity: translate("Actividad física"),
  };
  const dayFoods = {
    desayuno: "Desayuno",
    media_mañana: "Media Mañana",
    almuerzo: "Almuerzo",
    media_tarde: "Media Tarde",
    merienda: "Merienda",
    cena: "Cena",
    postre: "Postre",
    colacion: "Colación",
  };
  useEffect(() => {
    if (id !== null) {
      loadProtocol();
    }
  }, [id, reload, JSON.stringify(tableParams)]);

  const loadProtocol = async () => {
    setIsLoading(true);
    let data = {
      group_by: "day",
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    try {
      const res = await ProtocolServices.getProtocolDetails(id, data);
      if (res.status >= 200 && res.status < 300) {
        res.data.details = _.orderBy(res.data.details, "day", "asc");

        const newData = res.data.details.map((item, k) => {
          let name = "";
          if (item.form?.name) {
            name = item.form.name;
          }
          if (item.pill?.name) {
            name = item.pill.name;
          }
          if (item.rpm?.name) {
            name = item.rpm.name;
          }
          if (item.record && rpm[item.record]) {
            name = rpm[item.record];
          }
          return {
            ...item,
            key: k,
            name: name,
          };
        });
        setData(newData);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
        onChange?.(res.data.total);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const renderColCondition = (_, record) => {
    if (record.type !== "pill" && record.type !== "form")
      return (
        <Button disabled className="ant-btn-default">
          Agregar condición
        </Button>
      );
    return (
      <>
        {record.protocolcondition_set.map((cond, k) => (
          <div
            key={cond.id}
            className="py-1 px-4 border border-solid border-custom_gray rounded mb-2 relative flex flex-row"
          >
            <div className="flex flex-1 flex-col items-start h-8">
              <div>{cond.condition}</div>
              <Tag color="orange" className="leading-tight">
                {cond.type === "pill"
                  ? translate("Píldora")
                  : translate("Saltar")}
              </Tag>
            </div>
            <div className="flex w-6 items-center">
              <Popconfirm
                title="¿Estás seguro de eliminar el registro?"
                okText="Si"
                cancelText="No"
                onConfirm={() => {
                  if (onDeleteCondition) {
                    onDeleteCondition(cond);
                  }
                }}
              >
                <Tooltip title="Eliminar" color="blur">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    size="small"
                    className="absolute right-4"
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </div>
        ))}
        <Button
          onClick={() => {
            setSelected(record);
            setModalAdd(true);
          }}
          className="ant-btn-success"
        >
          Agregar condición
        </Button>
      </>
    );
  };

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 70,
      render: (text, record, index) => {
        return (
          <div className="flex space-x-2 ">
            <Popconfirm
              title="¿Estás seguro de eliminar el registro？"
              okText="Si"
              cancelText="No"
              onConfirm={() => {
                if (onDelete) {
                  onDelete(record);
                }
              }}
            >
              <Tooltip title="Eliminar" color="blur">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  className="border-0"
                />
              </Tooltip>
            </Popconfirm>
            {record.type === "pill" && (
              <Tooltip title="Ver" color="blur">
                <Button
                  onClick={() => {
                    setSelectedPill(record);
                  }}
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  className="border-0"
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Día",
      dataIndex: "day",
      key: "day",
      width: 70,
      sorter: { multiple: 6 },
    },
    {
      title: "Tipo",
      dataIndex: "type_name",
      key: "type_name",
      sorter: { multiple: 5 },
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: { multiple: 4 },
      render: (_, record) =>
        `${record.name}${
          record.feeding_day_food
            ? ` (${dayFoods[record.feeding_day_food]})`
            : ""
        }`,
    },
    {
      title: "Bloqueante",
      dataIndex: "flag_block",
      key: "flag_block",
      width: 100,
      render: (_, record) => (record.flag_block ? "Si" : "No"),
      sorter: { multiple: 3 },
    },
    {
      title: "Persistente",
      dataIndex: "persist",
      key: "persist",
      width: 100,
      render: (_, record) => {
        if (record.persist === null) return "No";
        return `${record.persist} ${translate("día{plural}", {
          plural: record.persist > 1 ? "s" : "",
        })}`;
      },
      sorter: { multiple: 7 },
    },
    {
      title: "Condición",
      dataIndex: "protocolcondition_set",
      key: "protocolcondition_set",
      render: renderColCondition,
      sorter: { multiple: 2 },
    },
    {
      title: "Hora de envío",
      dataIndex: "dispatch_time",
      key: "dispatch_time",
      render: (_, record) => record.dispatch_time?.substring(0, 5),
      sorter: { multiple: 1 },
    },
  ];

  const handleModalCancel = () => {
    setModalAdd(false);
  };
  const handleModalComplete = (item) => {
    // update user list
    setModalAdd(false);
    loadProtocol();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  return (
    <>
      <Table
        dataSource={data}
        columns={columns}
        rowKey={(i) => i.id.toString()}
        loading={
          isLoading || loading
            ? { indicator: antIcon, className: "text-custom_blue" }
            : false
        }
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />

      <Modal
        open={selectedPill !== null}
        title={translate("Píldora")}
        footer={
          <Button type="primary" onClick={() => setSelectedPill(null)}>
            Cerrar
          </Button>
        }
        className="flex flex-col"
        onCancel={() => {
          setSelectedPill(null);
        }}
      >
        <p>
          <strong>{translate("Tipo")}: </strong>{" "}
          {selectedPill?.pill?.type ?? "Sin tipo"}
        </p>
        <p>
          <strong>{translate("Nombre")}: </strong>{" "}
          {selectedPill?.pill?.name ?? "Sin nombre"}
        </p>
        <p>
          <strong>{translate("Descripción")}: </strong>{" "}
          {selectedPill?.pill?.description ?? "Sin descripción"}
        </p>
        <p>
          <strong>{translate("Url")}: </strong>{" "}
          {selectedPill?.pill?.url ? selectedPill?.pill?.url : "Sin url"}
        </p>
        <p>
          <strong>{translate("Strapi ID")}: </strong>{" "}
          {selectedPill?.pill?.capsule_strapi_id
            ? selectedPill?.pill?.capsule_strapi_id
            : "Sin Strapi ID"}
        </p>
      </Modal>
      <AddProtocolCondition
        visible={modalAdd}
        protocolDetails={selected}
        enterprises={enterprises ?? []}
        onCancel={() => {
          handleModalCancel();
        }}
        onComplete={(item) => {
          handleModalComplete(item);
        }}
      />
    </>
  );
};

ProtocolsActionDaysTable.propTypes = {
  id: PropTypes.number,
  reload: PropTypes.number,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onDeleteCondition: PropTypes.func,
  loading: PropTypes.bool,
  enterprises: PropTypes.array,
};

export default ProtocolsActionDaysTable;
